/**
 * DecisionAuthorityView
 *
 * The surface for a Decision Authority whose ball is currently out —
 * they sent the request down and are waiting for proposals to return.
 *
 * Shows:
 *   1. What I asked for (the intent I sent down)
 *   2. Lane status — which tier in which lane has the ball, and how long
 *   3. Incoming proposals when they return, with rationale + result summary
 *   4. Actions: Accept & ratify, Accept with conditions, Return for revision, Reject
 *
 * Embedded in CycleDetail Exchange tab when the user is executive-tier
 * and the ball is not with them.
 *
 * Canon reference: DOCS_V4/00-canon/00-build-contract.v3-planning-included.md §1D
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowRight,
  CheckCircle2,
  Clock,
  MessageSquare,
  RefreshCcw,
  Send,
  XCircle,
} from 'lucide-react';
import { useCatchballStore } from '../../../stores/prioritization/catchballStore';
import { useCatchballTierTurnStore } from '../../../stores/prioritization/catchballTierTurnStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useToast } from '../../ui/Toast';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';
import { useCatchballAcceptRight } from '../../../hooks/prioritization/useActRight';
import { refusedByRight } from '../../../lib/actRight';
import { EvaluatorIntentPanel } from './EvaluatorIntentPanel';
import { AllocationIfAccepted } from '../allocation/AllocationIfAccepted';
import { CallBehindProposal } from './CallBehindProposal';
import type { CatchballLoop } from '../../../types/prioritization/catchballLoop';
import type { CatchballProposal } from '../../../types/prioritization/catchballProposal';
import type { CatchballTierTurn } from '../../../types/prioritization/catchballTierTurn';

// Stable fallback — must be module-level so its reference never changes between renders.
// Using an inline `?? []` inside a Zustand selector creates a new array every render,
// which causes React's useSyncExternalStore to loop infinitely.
const EMPTY_TURNS: CatchballTierTurn[] = [];

// ── Lane status card ──────────────────────────────────────────────────────────

function LaneStatusCard({ loop }: { loop: CatchballLoop }) {
  const activePos = useCatchballTierTurnStore((s) => s.getActivePosition(loop.id));
  const allTurns  = useCatchballTierTurnStore((s) => s.getLoopState(loop.id)?.turns ?? EMPTY_TURNS);

  const hoursHeld = useMemo(() => {
    if (!activePos?.receivedAt) return null;
    const ms = Date.now() - new Date(activePos.receivedAt).getTime();
    return Math.round(ms / 3_600_000);
  }, [activePos?.receivedAt]);

  const stateLabel: Record<string, string> = {
    OPEN: 'Working',
    PROPOSED: 'Proposal submitted',
    REVISION_REQUESTED: 'Revision requested',
    ACCEPTED: 'Accepted',
    FINAL_REJECTED: 'Rejected',
    DECLINED: 'Closed',
  };

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-6 py-5">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          Where the ball is
        </h3>
        <Link
          to={`?tab=exchange`}
          className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium inline-flex items-center gap-1"
        >
          Full exchange <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {activePos ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-[var(--mustard)] animate-pulse flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Active — turn #{activePos.sequenceNumber}
              </p>
              {hoursHeld !== null && (
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] flex items-center gap-1 mt-0.5">
                  <Clock className="h-3 w-3" />
                  {hoursHeld < 24
                    ? `${hoursHeld}h elapsed`
                    : `${Math.round(hoursHeld / 24)}d elapsed`}
                </p>
              )}
            </div>
          </div>

          {/* Turn trail */}
          {allTurns.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {allTurns.map((turn, i) => (
                <span
                  key={turn.id}
                  className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                    turn.state === 'active'
                      ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]'
                      : turn.state === 'passed'
                      ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]'
                      : 'bg-[var(--surface)] text-[var(--text-ter)] border-[var(--rule)]'
                  }`}
                >
                  {turn.ballDirection === 'down' ? (
                    <ArrowDown className="h-2.5 w-2.5" />
                  ) : (
                    <ArrowRight className="h-2.5 w-2.5" />
                  )}
                  Turn {i + 1}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-[var(--text-ter)]">
          {stateLabel[loop.state as string] ?? loop.state}
        </p>
      )}
    </div>
  );
}

// ── Proposal review card ──────────────────────────────────────────────────────

function ProposalReviewCard({
  proposal,
  loop,
  cycleId,
  onAccept,
  onAcceptProvisional,
  onReturn,
  onReject,
}: {
  proposal: CatchballProposal;
  loop: CatchballLoop;
  cycleId: string;
  onAccept: (id: string) => Promise<void>;
  onAcceptProvisional: (id: string) => Promise<void>;
  onReturn: (id: string, note: string) => Promise<void>;
  onReject: (id: string, note: string) => Promise<void>;
}) {
  const [returnNote, setReturnNote] = useState('');
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  // D-4 (migration 987): who may accept is the database's answer — the rule
  // catchball_accept acts on, including the cycle's "Items / selection"
  // decision right when one is set. Asked only while the proposal is pending.
  const acceptRight = useCatchballAcceptRight(proposal.status === 'pending' ? proposal.id : null);

  const statusClass =
    proposal.status === 'accepted'
      ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]'
      : proposal.status === 'rejected'
      ? 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]'
      : proposal.status === 'countered'
      ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]'
      : 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]';

  const handle = async (fn: () => Promise<void>) => {
    setIsBusy(true);
    try { await fn(); } finally { setIsBusy(false); }
  };

  const isPending = proposal.status === 'pending';

  return (
    <div className="border border-[var(--slate)] dark:border-[var(--slate)]/40 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-6 py-5 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-0.5">
            Incoming proposal
          </p>
          <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            From {proposal.proposedByOrgUnitName ?? proposal.proposedByName ?? 'Team'}
          </p>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            {new Date(proposal.proposedAt).toLocaleDateString(undefined, {
              month: 'short', day: 'numeric', year: 'numeric',
            })}
          </p>
        </div>
        <span className={`inline-flex items-center text-[11px] font-medium px-2.5 py-1 rounded-full border capitalize ${statusClass}`}>
          {proposal.status}
        </span>
      </div>

      {/* Rationale */}
      {proposal.rationale && (
        <div className="bg-[var(--surface)] dark:bg-[var(--bg-raised)]/30 px-4 py-3">
          <p className="text-xs font-semibold text-[var(--text-ter)] mb-1">Rationale</p>
          <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-pri)]">{proposal.rationale}</p>
        </div>
      )}

      {/* Link to full session results */}
      {loop.finalS6SessionResultId && (
        <Link
          to={`/prioritization/cycles/${cycleId}?tab=results`}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
        >
          View ranked results <ArrowRight className="h-3 w-3" />
        </Link>
      )}

      {/* Actions — only when proposal is pending */}
      {isPending && (
        <div className="space-y-3 pt-1 border-t border-[var(--rule)] dark:border-[var(--rule)]">
          {/* The call behind what is about to be accepted: a standing is never drawn alone */}
          {!showReturnForm && !showRejectForm && (
            <CallBehindProposal proposal={proposal} />
          )}

          {/* Cell Allocation: how the shares would stand (C-2, migration 972) */}
          {!showReturnForm && !showRejectForm && cycleId && (
            <AllocationIfAccepted cycleId={cycleId} proposalId={proposal.id} />
          )}

          {/* Primary actions */}
          {!showReturnForm && !showRejectForm && (
            <div className="flex flex-wrap gap-2">
              {acceptRight.line && (
                <p className="basis-full text-sm text-[var(--text-ter)]">{acceptRight.line}</p>
              )}
              {acceptRight.allowed && (
                <button
                  disabled={isBusy}
                  onClick={() => handle(() => onAccept(proposal.id))}
                  className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 bg-[var(--green)] hover:bg-[var(--green)] text-white transition-colors disabled:opacity-40"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {CATCHBALL_LABELS.acceptAndRatify}
                </button>
              )}
              {acceptRight.allowedProvisional && (
                <button
                  disabled={isBusy}
                  onClick={() => handle(() => onAcceptProvisional(proposal.id))}
                  className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] transition-colors disabled:opacity-40"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {CATCHBALL_LABELS.acceptWithConditions}
                </button>
              )}
              {/* 1001: rejecting and returning exercise the right accepting does — not drawn when it shuts this person out (build contract §2.8) */}
              {!refusedByRight(acceptRight) && (
                <>
                  <button
                    disabled={isBusy}
                    onClick={() => setShowReturnForm(true)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 border border-[var(--mustard)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors disabled:opacity-40"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    {CATCHBALL_LABELS.returnForRevision}
                  </button>
                  <button
                    disabled={isBusy}
                    onClick={() => setShowRejectForm(true)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 border border-[var(--coral)] text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors disabled:opacity-40"
                  >
                    <XCircle className="h-4 w-4" />
                    {CATCHBALL_LABELS.reject}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Return form */}
          {showReturnForm && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                  What do you need from them?
                </label>
                <textarea
                  value={returnNote}
                  onChange={(e) => setReturnNote(e.target.value)}
                  rows={3}
                  placeholder="Explain what was missing or what you need clarified before you can accept…"
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={isBusy || !returnNote.trim()}
                  onClick={() => handle(() => onReturn(proposal.id, returnNote))}
                  className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 bg-[var(--mustard)] hover:bg-[var(--mustard)] text-white transition-colors disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                  Confirm return
                </button>
                <button
                  onClick={() => { setShowReturnForm(false); setReturnNote(''); }}
                  className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] px-3 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Reject form */}
          {showRejectForm && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                  Reason for rejection
                </label>
                <textarea
                  value={returnNote}
                  onChange={(e) => setReturnNote(e.target.value)}
                  rows={3}
                  placeholder="Explain why this proposal cannot be accepted…"
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={isBusy || !returnNote.trim()}
                  onClick={() => handle(() => onReject(proposal.id, returnNote))}
                  className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 bg-[var(--coral)] hover:bg-[var(--coral)] text-white transition-colors disabled:opacity-40"
                >
                  <XCircle className="h-4 w-4" />
                  Confirm reject
                </button>
                <button
                  onClick={() => { setShowRejectForm(false); setReturnNote(''); }}
                  className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] px-3 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface DecisionAuthorityViewProps {
  cycleId: string;
  loop: CatchballLoop;
  cycleOwnerName?: string;
}

export function DecisionAuthorityView({
  cycleId,
  loop,
  cycleOwnerName,
}: DecisionAuthorityViewProps) {
  const userId = useUserStore((s) => s.userId) ?? '';
  const { showToast } = useToast();

  const workspaceId = useDegapProcessStore((s) =>
    cycleId ? s.getCycle(cycleId)?.workspaceId ?? '' : '',
  );

  const allProposals = useCatchballStore((s) => s.proposals);
  const acceptProposal = useCatchballStore((s) => s.acceptProposal);
  const acceptProposalWithConditions = useCatchballStore((s) => s.acceptProposalWithConditions);
  const returnProposal = useCatchballStore((s) => s.returnProposal);
  const rejectProposal = useCatchballStore((s) => s.rejectProposal);

  const loopProposals = useMemo(
    () =>
      Object.values(allProposals).filter(
        (p) => p.loopId === loop.id && p.direction === 'up',
      ),
    [allProposals, loop.id],
  );

  const pendingProposals = loopProposals.filter((p) => p.status === 'pending');
  const resolvedProposals = loopProposals.filter((p) => p.status !== 'pending');

  const baseParams = { loopId: loop.id, cycleId, actorId: userId, workspaceId };

  // Shown after an acceptance so the decision-maker can go read what was recorded,
  // without being taken there mid-queue.
  const [lastAcceptedAt, setLastAcceptedAt] = useState<number | null>(null);

  // The store actions return false (they never throw) when the server refuses;
  // the reason is kept on the store's `error` field.
  const failureReason = (fallback: string) =>
    useCatchballStore.getState().error || fallback;

  const handleAccept = async (proposalId: string) => {
    try {
      const ok = await acceptProposal({ ...baseParams, proposalId });
      if (!ok) {
        showToast('error', 'Not accepted', failureReason('The proposal was not ratified. Try again.'));
        return;
      }
      // Accepting records the outcome to this cycle's Plan of Record. Say so, and
      // offer the way there — the standalone review page navigated there outright,
      // which stranded a decision-maker who had more proposals still to work.
      showToast('success', 'Accepted and ratified', "Recorded to this cycle's report.");
      setLastAcceptedAt(Date.now());
    } catch (err) {
      showToast('error', 'Not accepted', err instanceof Error ? err.message : 'Could not accept proposal.');
    }
  };

  const handleAcceptProvisional = async (proposalId: string) => {
    try {
      const ok = await acceptProposalWithConditions({
        ...baseParams,
        proposalId,
        conditions: 'Accepted provisionally — final acceptance pending confirmation.',
      });
      if (!ok) {
        showToast('error', 'Not accepted', failureReason('The proposal was not accepted. Try again.'));
        return;
      }
      showToast('success', 'Accepted', 'Proposal accepted provisionally.');
    } catch (err) {
      showToast('error', 'Not accepted', err instanceof Error ? err.message : 'Could not accept proposal.');
    }
  };

  const handleReturn = async (proposalId: string, note: string) => {
    try {
      const ok = await returnProposal({ ...baseParams, proposalId, revisionGuidance: note });
      if (!ok) {
        showToast('error', 'Not returned', failureReason('The proposal was not sent back. Try again.'));
        return;
      }
      showToast('success', 'Returned', 'Proposal sent back with your notes.');
    } catch (err) {
      showToast('error', 'Not returned', err instanceof Error ? err.message : 'Could not return proposal.');
    }
  };

  const handleReject = async (proposalId: string, note: string) => {
    try {
      const ok = await rejectProposal({ ...baseParams, proposalId, rationale: note });
      if (!ok) {
        showToast('error', 'Not rejected', failureReason('The proposal was not rejected. Try again.'));
        return;
      }
      showToast('success', 'Rejected', 'Proposal rejected.');
    } catch (err) {
      showToast('error', 'Not rejected', err instanceof Error ? err.message : 'Could not reject proposal.');
    }
  };

  return (
    <div className="space-y-5">
      {lastAcceptedAt !== null && (
        <div
          className="flex items-center justify-between gap-3 border bg-[var(--green-dim)] px-4 py-2"
          style={{ borderColor: 'var(--green)' }}
        >
          <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-[var(--green)] dark:text-[var(--text-ter)]">
            Recorded to the cycle report
          </span>
          <Link
            to={`/prioritization/cycles/${cycleId}/report`}
            className="whitespace-nowrap font-mono text-[0.72rem] uppercase tracking-[0.08em] text-[var(--green)] dark:text-[var(--text-ter)] hover:opacity-80"
          >
            Open cycle report →
          </Link>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-0.5">
            Decision Authority
          </p>
          <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Your request is in progress
          </h2>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
            {pendingProposals.length > 0
              ? `${pendingProposals.length} proposal${pendingProposals.length !== 1 ? 's' : ''} waiting for your review`
              : 'Waiting for the team to complete their work and return a proposal.'}
          </p>
        </div>
        <Link
          to={`/prioritization/cycles/${cycleId}?tab=exchange`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] border border-[var(--slate)] px-3 py-1.5 transition-colors"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Full exchange
        </Link>
      </div>

      {/* What I asked for */}
      {loop.intent && (
        <EvaluatorIntentPanel
          intent={loop.intent}
          requestFromLabel="You"
          evaluatorName={cycleOwnerName}
          tossedAt={loop.requestIssuedAt?.toISOString()}
        />
      )}

      {/* Lane status */}
      <LaneStatusCard loop={loop} />

      {/* Incoming proposals — pending first */}
      {loopProposals.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {pendingProposals.length > 0 ? 'Proposals awaiting your decision' : 'Proposals'}
          </h3>
          {pendingProposals.map((p) => (
            <ProposalReviewCard
              key={p.id}
              proposal={p}
              loop={loop}
              cycleId={cycleId}
              onAccept={handleAccept}
              onAcceptProvisional={handleAcceptProvisional}
              onReturn={handleReturn}
              onReject={handleReject}
            />
          ))}
          {resolvedProposals.length > 0 && (
            <details className="group">
              <summary className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] cursor-pointer list-none flex items-center gap-1">
                <span>{resolvedProposals.length} resolved proposal{resolvedProposals.length !== 1 ? 's' : ''}</span>
                <ArrowRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="mt-2 space-y-2">
                {resolvedProposals.map((p) => (
                  <ProposalReviewCard
                    key={p.id}
                    proposal={p}
                    loop={loop}
                    cycleId={cycleId}
                    onAccept={handleAccept}
                    onAcceptProvisional={handleAcceptProvisional}
                    onReturn={handleReturn}
                    onReject={handleReject}
                  />
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Empty state when no proposals yet */}
      {loopProposals.length === 0 && (
        <div className="border border-dashed border-[var(--rule)] dark:border-[var(--rule)] px-6 py-8 text-center">
          <Clock className="h-6 w-6 text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Waiting for proposals
          </p>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
            The team is working. Proposals will appear here when they submit.
          </p>
        </div>
      )}
    </div>
  );
}

export default DecisionAuthorityView;
